import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const DEFAULT_CHROME_HEIGHT = 100;
const DEFAULT_ASPECT_RATIO = 4 / 5;

const useResponsiveColumns = (columns) => {
  const theme = useTheme();
  const isXl = useMediaQuery(theme.breakpoints.up("xl"));
  const isLg = useMediaQuery(theme.breakpoints.up("lg"));
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const isSm = useMediaQuery(theme.breakpoints.up("sm"));

  if (typeof columns === "number") return columns;

  const order = ["xs", "sm", "md", "lg", "xl"];
  const activeIndex = isXl ? 4 : isLg ? 3 : isMd ? 2 : isSm ? 1 : 0;
  for (let i = activeIndex; i >= 0; i--) {
    const value = columns?.[order[i]];
    if (typeof value === "number") return value;
  }
  return 1;
};

/**
 * Virtualized masonry: only cards near the viewport of `scrollElementRef` are
 * mounted. Each entry is `{ key, aspectRatio?, estimateHeight? }`; card height
 * is estimated from the media aspect ratio plus the measured "chrome" (strips
 * above/below the media) and then corrected with measureElement.
 */
const VirtualMasonry = ({
  entries = [],
  renderEntry,
  scrollElementRef,
  columns = { xs: 1, sm: 2, md: 3, lg: 4, xl: 5 },
  spacing = 2,
  overscanRows = 2,
  pinnedKey = null,
}) => {
  const theme = useTheme();
  const lanes = Math.max(1, useResponsiveColumns(columns));
  const gap = parseFloat(theme.spacing(spacing)) || 0;

  const listRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [scrollMargin, setScrollMargin] = useState(0);
  const chromeHeightRef = useRef(DEFAULT_CHROME_HEIGHT);

  const columnWidth =
    containerWidth > 0
      ? Math.max(0, (containerWidth - gap * (lanes - 1)) / lanes)
      : 0;

  const updateGeometry = useCallback(() => {
    const listEl = listRef.current;
    const scrollEl = scrollElementRef?.current;
    if (!listEl) return;
    setContainerWidth(listEl.clientWidth);
    if (scrollEl) {
      const offset =
        listEl.getBoundingClientRect().top -
        scrollEl.getBoundingClientRect().top +
        scrollEl.scrollTop;
      setScrollMargin((prev) =>
        Math.abs(prev - offset) < 0.5 ? prev : Math.round(offset)
      );
    }
  }, [scrollElementRef]);

  // Content above the list (folder bar, uploads...) can change height, which
  // moves the list inside the scroll element: observe the scroll element's
  // children as well as the list itself.
  useLayoutEffect(() => {
    updateGeometry();
    const listEl = listRef.current;
    const scrollEl = scrollElementRef?.current;
    if (typeof ResizeObserver === "undefined" || !listEl) return undefined;
    const observer = new ResizeObserver(() => updateGeometry());
    observer.observe(listEl);
    if (scrollEl) {
      observer.observe(scrollEl);
      Array.from(scrollEl.children).forEach((child) => observer.observe(child));
    }
    return () => observer.disconnect();
  }, [scrollElementRef, updateGeometry]);

  const keys = useMemo(() => entries.map((entry) => entry.key), [entries]);
  const getItemKey = useCallback((index) => keys[index] ?? index, [keys]);

  const estimateSize = useCallback(
    (index) => {
      const entry = entries[index];
      if (!entry || columnWidth <= 0) return 400;
      if (typeof entry.estimateHeight === "function") {
        return entry.estimateHeight(columnWidth);
      }
      const aspectRatio = entry.aspectRatio || DEFAULT_ASPECT_RATIO;
      return columnWidth / aspectRatio + chromeHeightRef.current;
    },
    [entries, columnWidth]
  );

  const pinnedIndex = useMemo(
    () => (pinnedKey == null ? -1 : keys.indexOf(pinnedKey)),
    [keys, pinnedKey]
  );

  const rangeExtractor = useCallback(
    (range) => {
      const result = [];
      for (let i = range.startIndex; i <= range.endIndex; i++) result.push(i);
      // Keep the card being dragged mounted so its dragend still fires.
      if (pinnedIndex >= 0 && !result.includes(pinnedIndex)) {
        result.push(pinnedIndex);
      }
      return result;
    },
    [pinnedIndex]
  );

  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => scrollElementRef?.current ?? null,
    estimateSize,
    getItemKey,
    lanes,
    gap,
    scrollMargin,
    overscan: lanes * overscanRows,
    rangeExtractor,
    enabled: columnWidth > 0,
    useFlushSync: false,
    // In multi-lane layouts a size change only shifts one column, so
    // compensating the scroll position would make the other columns jump.
    shouldAdjustScrollPositionOnItemSizeChange: (item, _delta, instance) =>
      lanes === 1 &&
      item.end <= (instance.scrollOffset ?? 0) - instance.options.scrollMargin,
  });

  // Lane assignments are cached by index inside the virtualizer. When items
  // are inserted/removed anywhere but the end (placeholders at the top,
  // deletions), indices shift and the cache would place cards in stale lanes.
  const prevKeysRef = useRef(keys);
  if (prevKeysRef.current !== keys) {
    const prevKeys = prevKeysRef.current;
    const isAppendOnly =
      prevKeys.length <= keys.length &&
      prevKeys.every((key, index) => keys[index] === key);
    if (!isAppendOnly) {
      virtualizer.laneAssignments?.clear?.();
      // Force a full recompute so no measurement keyed by an old index is reused.
      virtualizer.pendingMin = null;
    }
    prevKeysRef.current = keys;
  }

  // Columns change → widths change → every measured height is stale.
  const prevColumnWidthRef = useRef(columnWidth);
  useEffect(() => {
    if (Math.abs(prevColumnWidthRef.current - columnWidth) > 1) {
      prevColumnWidthRef.current = columnWidth;
      virtualizer.measure();
    }
  }, [columnWidth, virtualizer]);

  const virtualItems = virtualizer.getVirtualItems();

  // Learn the non-media height of a card from the ones already measured so
  // estimates for unmeasured cards are close and layout barely shifts.
  useLayoutEffect(() => {
    if (columnWidth <= 0) return;
    const sizeCache = virtualizer.itemSizeCache;
    if (!sizeCache) return;
    const samples = [];
    virtualItems.forEach((virtualItem) => {
      const entry = entries[virtualItem.index];
      if (!entry || entry.sampleChrome === false || !entry.aspectRatio) return;
      const measured = sizeCache.get(virtualItem.key);
      if (typeof measured !== "number") return;
      const chrome = measured - columnWidth / entry.aspectRatio;
      if (chrome >= 0 && chrome < 400) samples.push(chrome);
    });
    if (samples.length > 0) {
      samples.sort((a, b) => a - b);
      chromeHeightRef.current = samples[Math.floor(samples.length / 2)];
    }
  });

  return (
    <Box
      ref={listRef}
      sx={{
        position: "relative",
        width: "100%",
        height: virtualizer.getTotalSize(),
      }}
    >
      {columnWidth > 0 &&
        virtualItems.map((virtualItem) => {
          const entry = entries[virtualItem.index];
          if (!entry) return null;
          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: virtualItem.lane * (columnWidth + gap),
                width: columnWidth,
                transform: `translateY(${virtualItem.start - virtualizer.options.scrollMargin}px)`,
              }}
            >
              {renderEntry(entry, virtualItem.index)}
            </div>
          );
        })}
    </Box>
  );
};

export default VirtualMasonry;
